import { flushWindowToRenderer } from '@src/editor/lib/flushWindowToRenderer';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { makeAutoObservable, runInAction } from 'mobx';

import { resolveSceneDetectionsForEdit } from '../services/framing/resolveSceneDetectionsForEdit';
import {
  type ISceneFramingService,
  SmartFramingService,
} from '../services/framing/SmartFramingService';
import { SpeakerAwareSceneFramer } from '../services/framing/SpeakerAwareSceneFramer';
import { SceneService } from '../services/SceneService';
import { KARAOKE_TRACK_NUMBER } from './constants';
import {
  clampToLastFrame,
  collectWindowElements,
  DEFAULT_WINDOWING_CONFIG,
  windowCoversTime,
} from './lib/TimelineWindowFilter';
import { VideoElement } from './models';
import { LayoutEngine } from './models/layout/LayoutEngine';
import {
  parseKaraokeDataFromLegacyTranscriptElements,
  shiftLegacyTranscriptAttributesFromWhitespaces,
} from './services/legacyKaraokeImportExport';
import RendererSyncService, {
  resolveResetProps,
  resolveResetPropsMap,
} from './services/RendererSyncService';
import { SceneInitializationService } from './services/SceneInitializationService';
import { migrateBasicTemplateTextPositions } from './services/SceneTextPositionMigration';
import { EditorChangeType } from './types';
import { VideoEditor } from './VideoEditor';

import type { TimelineMode } from './commands/TimelineMode';
import type { IVideoEditor } from './interface/IVideoEditor';
import type {
  PlaybackCallbacks,
  WindowBounds,
  WindowingConfig,
} from './lib/TimelineWindowFilter';
import type { Element } from './models/Element';
import type {
  IScene,
  LayoutOptions,
  LayoutStrategy,
} from './models/layout/types';
import type { Timeline, TimelineJSON } from './models/Timeline';
import type { Track } from './models/Track';
import type { KaraokeData } from './types/karaoke';
import type { SceneDetection } from './types/sceneDetection';
import type { ElementState } from '@src/renderer/ElementState';
import type { RendererState } from '@src/renderer/RendererState';
import type {
  KaraokeStore,
  TranscriptionStore,
  VideoSource,
} from '@src/stores-v2';
import type SceneLayoutIndexStore from '@src/stores-v2/SceneLayoutIndexStore';
import type { VideoSourceStore } from '@src/stores-v2/VideoSourceStore';
import type { Video } from '@src/types.ts/story';
import type {
  TranscriptElementLegacy,
  TranscriptElementWithClipSegmentAttributes,
} from '@src/types.ts/transcript';

const logger = LogManager.getLogger('EditorStore');

function hasKaraokeTrackInSource(videoSource: VideoSource) {
  return !!videoSource.elements?.find(el => el.track === KARAOKE_TRACK_NUMBER);
}

interface IPunchListStore {
  syncFromEditor: (editor: IVideoEditor) => void;
}

/**
 * EditorStore is a MobX wrapper around VideoEditor for reactivity
 */
export class EditorStore {
  // Core state
  editor: IVideoEditor;
  timeline: Timeline;
  isReady: boolean;
  activeTranscript: any[] = [];

  /**
   * Version counter that increments when the timeline changes.
   * Used to trigger MobX reactivity for computed properties that read
   * from the non-observable VideoEditor.timeline.tracks.
   */
  private timelineVersion: number = 0;

  transcriptSegmentArchitectureNotSupported: boolean = false;
  transcriptSegmentArchitectureMigrationLog: {
    status: 'ok' | 'warning' | 'not_supported' | 'error' | 'none';
    debugInfo?: object;
  } | null = null;

  // Headless integration control
  private isLocalChange = false;
  private externalChangeCount = 0;

  // Selection state
  selectedElementIds: string[] = [];

  private videoSourceStore: VideoSourceStore;
  private transcriptionStore: TranscriptionStore;
  private karaokeStore: KaraokeStore;
  private sceneLayoutIndexStore: SceneLayoutIndexStore;
  private punchListStore?: IPunchListStore;

  // Scene layout sync services
  rendererSyncService?: RendererSyncService;
  private rendererExternalSyncService?: RendererSyncService;

  // Smart framing service (lazily initialized)
  private _framingService?: ISceneFramingService;
  private readonly getSpeakerMap: () => Record<string, string[]> | null;

  // Layout strategy configuration (set from feature flags)
  private _layoutStrategy: LayoutStrategy = 'hybrid';
  private _sceneCountThreshold: number = 100;

  /**
   * Cached split-window from the last `toPreviewJSON` call.
   * Used by hybrid-adaptive to decide whether a playhead movement
   * crosses a range boundary and requires a source refresh.
   */
  private _splitWindow?: { startTime: number; endTime: number };

  /** Cached windowing bounds from the last collectWindowElements call. */
  private _windowBounds?: WindowBounds;

  /** Timeline windowing configuration (set from feature flags). */
  private _windowingConfig: WindowingConfig = DEFAULT_WINDOWING_CONFIG;

  constructor(
    videoSourceStore: VideoSourceStore,
    transcriptionStore: TranscriptionStore,
    karaokeStore: KaraokeStore,
    sceneLayoutIndexStore: SceneLayoutIndexStore,
    /**
     * The open story's speaker→face map (null when unavailable or framing is
     * face-only) — lets scenes created mid-edit frame by speaker.
     */
    getSpeakerMap: () => Record<string, string[]> | null = () => null,
  ) {
    this.getSpeakerMap = getSpeakerMap;
    this.videoSourceStore = videoSourceStore;
    this.transcriptionStore = transcriptionStore;
    this.karaokeStore = karaokeStore;
    this.sceneLayoutIndexStore = sceneLayoutIndexStore;
    this.editor = new VideoEditor();
    this.timeline = this.editor.timeline;

    this.isReady = runInAction(() => false);

    this.initializeEditor();

    makeAutoObservable(this, {
      // rootStore: false,
      editor: false,
      getSpeakerMap: false,
    } as any);
  }

  /**
   * Initializes the editor with observers
   */
  initializeEditor(): void {
    // Register MobX reactivity observer
    this.setupEditorObservers();
  }

  private _playbackCallbacks?: PlaybackCallbacks;

  /** Convenience accessor — returns 0 when no callback is wired. */
  private get _currentTime(): number {
    return this._playbackCallbacks?.getCurrentTime() ?? 0;
  }

  initializePlaybackSync(playbackCallbacks?: PlaybackCallbacks) {
    this._playbackCallbacks = playbackCallbacks;
    this.initializeSceneLayoutSyncServices();
  }

  /**
   * Initialize scene layout sync services
   */
  private initializeSceneLayoutSyncServices(): void {
    // Service for external changes (from iframe) - wraps with acceptExternalChanges
    this.rendererExternalSyncService = new RendererSyncService({
      layoutIndexStore: this.sceneLayoutIndexStore,
      getScene: sceneId => {
        return this.editor.timeline.findSceneById(sceneId);
      },
      updateScene: (sceneId, updates) => {
        try {
          return this.acceptExternalChanges(() => {
            return this.editor.commandUpdateScene(sceneId, updates);
          });
        } catch (e) {
          logger.error(
            'Error updating single scene in RendererSync (external)',
            extractErrorInfo(e),
          );
          return false;
        }
      },
      updateScenes: updates => {
        try {
          this.acceptExternalChanges(() => {
            this.editor.commandUpdateScenes(updates);
          });
        } catch (e) {
          logger.error(
            'Error updating multiple scenes in RendererSync (external)',
            extractErrorInfo(e),
          );
        }
      },
      updateElement: (elementId, updates) => {
        try {
          return this.acceptExternalChanges(() => {
            return this.editor.commandUpdateElement(
              elementId,
              resolveResetProps(this.editor.getElementById(elementId), updates),
            );
          });
        } catch (e) {
          logger.error(
            'Error updating single element in RendererSync (external)',
            extractErrorInfo(e),
          );
          return false;
        }
      },
      updateElements: updates => {
        try {
          this.acceptExternalChanges(() => {
            this.editor.commandUpdateMultipleElements(
              resolveResetPropsMap(updates, id =>
                this.editor.getElementById(id),
              ),
            );
          });
        } catch (e) {
          logger.error(
            'Error updating multiple elements in RendererSync (external)',
            extractErrorInfo(e),
          );
        }
      },
      normalizeRendererState: state => state,
      getCurrentTime: () => this._currentTime,
    });

    // Service for programmatic changes - direct updates without acceptExternalChanges
    this.rendererSyncService = new RendererSyncService({
      layoutIndexStore: this.sceneLayoutIndexStore,
      getScene: sceneId => {
        return this.editor.timeline.findSceneById(sceneId);
      },
      updateScene: (sceneId, updates) => {
        try {
          // Direct update without acceptExternalChanges wrapper
          return this.editor.commandUpdateScene(sceneId, updates);
        } catch (e) {
          logger.error(
            'Error updating single scene in RendererSync',
            extractErrorInfo(e),
          );
          return false;
        }
      },
      updateScenes: updates => {
        try {
          // Direct update without acceptExternalChanges wrapper
          this.editor.commandUpdateScenes(updates);
        } catch (e) {
          logger.error(
            'Error updating multiple scenes in RendererSync',
            extractErrorInfo(e),
          );
        }
      },
      normalizeRendererState: state => state,
      getCurrentTime: () => this._currentTime,
    });
  }

  /**
   * Sync external changes from iframe to editor state
   * Only call this for user-driven changes from the iframe preview
   */
  syncExternalLayoutChanges(
    prevState: RendererState | undefined,
    newState: RendererState,
  ): void {
    if (!this.rendererExternalSyncService) {
      logger.warn('External sync service not initialized');
      return;
    }
    const changeCountBefore = this.externalChangeCount;
    this.rendererExternalSyncService.sync(prevState, newState);

    // PLAT-10700: when sync applies a manual edit AND an outbound setSource is
    // still queued in the debounce, that queued payload is a PRE-edit snapshot
    // (e.g. from an in-flight windowing/adaptive refresh) that would flush and
    // revert the change. Regenerate it from the now-updated model so it flushes
    // with the edit applied on top of the queued content — the queued change is
    // preserved, not dropped. When nothing is queued we send nothing: the
    // renderer already reflects the edit, and a fresh push would only fight the
    // user's next edit.
    if (
      this.externalChangeCount > changeCountBefore &&
      this.videoSourceStore.hasPendingSetSource
    ) {
      this.refreshTimeline();
    }
  }

  private acceptExternalChanges<T>(action: () => T): T {
    try {
      this.isLocalChange = true;
      this.externalChangeCount++;
      return action();
    } finally {
      this.isLocalChange = false;
    }
  }

  /**
   * Setup observers to ensure MobX reactivity for editor changes
   */
  private setupEditorObservers(): void {
    const mobxReactivityObserver = {
      onEditorChange: (editor: VideoEditor, changeType: EditorChangeType) => {
        logger.info('OnEditorChange', changeType);

        if (changeType === EditorChangeType.TRACKS_RENUMBERED) {
          const previousIsLocalChange = this.isLocalChange;
          try {
            this.isLocalChange = true;
            this.refreshTimeline();
          } finally {
            this.isLocalChange = previousIsLocalChange;
          }
        }

        if (
          changeType === EditorChangeType.TIMELINE_CHANGED ||
          changeType === EditorChangeType.ELEMENT_ADDED ||
          changeType === EditorChangeType.ELEMENT_REMOVED ||
          changeType === EditorChangeType.ELEMENT_UPDATED
        ) {
          // Increment version counter to trigger MobX reactivity for tracksMap
          runInAction(() => {
            this.timelineVersion++;
          });

          if (this.isLocalChange) return;
          try {
            this.isLocalChange = true;
            this.refreshTimeline();
            this.refreshTranscript();
            this.refreshKaraoke();
          } finally {
            this.isLocalChange = false;
          }
        }

        if (changeType === EditorChangeType.PUNCH_LIST_UPDATED) {
          this.refreshPunchList();
        }

        // Handle forced sync - only if external changes actually occurred
        if (changeType === EditorChangeType.FORCED_REFRESH) {
          if (this.isLocalChange || this.externalChangeCount === 0) return;
          logger.info(
            `FORCED_REFRESH processing ${this.externalChangeCount} external changes`,
          );
          try {
            this.isLocalChange = true;
            this.refreshTimeline();
            this.refreshTranscript();
            this.refreshKaraoke();
            this.externalChangeCount = 0;
          } finally {
            this.isLocalChange = false;
          }
          return;
        }
      },
    };

    this.editor.registerObserver(mobxReactivityObserver);
  }

  /**
   * Configure the layout strategy used by `refreshTimeline` (preview path).
   * Called from the stores layer when LaunchDarkly flags change.
   */
  setPreviewLayoutConfig(config: {
    strategy?: LayoutStrategy;
    sceneCountThreshold?: number;
  }): void {
    if (config.strategy !== undefined) this._layoutStrategy = config.strategy;
    if (config.sceneCountThreshold !== undefined)
      this._sceneCountThreshold = config.sceneCountThreshold;
    logger.debug('[setPreviewLayoutConfig] updated', {
      strategy: this._layoutStrategy,
      sceneCountThreshold: this._sceneCountThreshold,
    });
  }

  getPreviewLayoutConfig(): {
    strategy: LayoutStrategy;
    sceneCountThreshold: number;
  } {
    return {
      strategy: this._layoutStrategy,
      sceneCountThreshold: this._sceneCountThreshold,
    };
  }

  /**
   * Configure the timeline windowing filter used by `refreshTimeline`.
   * Called from the stores layer when LaunchDarkly flags change.
   */
  setWindowingConfig(config: WindowingConfig): void {
    this._windowingConfig = config;
    logger.info('[setWindowingConfig] updated', this._windowingConfig);
  }

  get isWindowingActive(): boolean {
    return this._windowingConfig.enabled;
  }

  /**
   * Returns `true` when hybrid-adaptive is active and the given time falls
   * outside the cached split-range window — meaning a `refreshTimeline()`
   * call is needed to recalculate the layout around the new playback position.
   *
   * This can be called from a debounced MobX reaction watching
   * `videoCreator.time` in the stores layer.
   */
  needsHybridAdaptiveRefresh(currentTime: number): boolean {
    if (this._layoutStrategy !== 'hybrid-adaptive') return false;
    if (!this._splitWindow) return true; // never computed yet
    return (
      currentTime < this._splitWindow.startTime ||
      currentTime > this._splitWindow.endTime
    );
  }

  /**
   * Returns `true` when timeline windowing is active and the playhead is
   * approaching or has moved outside the current window bounds.
   */
  needsWindowingRefresh(currentTime: number): boolean {
    if (!this.isWindowingActive) return false;
    if (!this._windowBounds) return true;
    const { recheckThreshold } = this._windowingConfig.playback;
    return (
      currentTime >= this._windowBounds.timeB - recheckThreshold ||
      currentTime < this._windowBounds.timeA ||
      currentTime > this._windowBounds.timeB
    );
  }

  /**
   * Guarantee the renderer holds `time` before it is asked to present it.
   *
   * Windowing sends the renderer only the elements around the playhead, and it
   * recomputes that window on every layout refresh — around whatever position
   * the playhead held at that moment. So a refresh triggered by a structural
   * edit or by playback leaves the renderer holding a source that begins
   * wherever the playhead happened to be. Seeking or playing into a position
   * the window excludes then asks the renderer to present footage it was never
   * sent, and it shows black.
   *
   * The recovery already existed — `needsWindowingRefresh` turns true once the
   * playhead falls outside the bounds — but it *trails* the command by a 300 ms
   * debounce (and is skipped entirely during playback), so the renderer starts
   * on content it does not have and the refresh chases it. Leading the command
   * instead of trailing it is what removes the black: rebuild the window around
   * the target and flush it, then let the caller seek or play.
   *
   * Callers on the scrub path must skip this — a scrub issues seeks
   * continuously, and the scrubbing window has its own debounced reaction
   * (`AdaptivePlaybackSourceManager`) for exactly that reason.
   */
  async ensureWindowCovers(time: number): Promise<void> {
    if (this.windowCovers(time)) return;
    this.refreshTimeline(time);
    await flushWindowToRenderer(this.videoSourceStore);
  }

  /**
   * Whether the renderer already holds `time`.
   *
   * Windowing off means it holds everything. Otherwise this is plain
   * containment — see `windowCoversTime` for why it must not be
   * `needsWindowingRefresh`.
   */
  private windowCovers(time: number): boolean {
    if (!this.isWindowingActive) return true;
    return windowCoversTime(this._windowBounds, time);
  }

  /**
   * Apply the timeline windowing filter to the serialized source,
   * replacing its elements with only those in the visible window.
   *
   * `atTime` overrides the playhead the window is centred on, so a seek can
   * build the window around where it is going rather than where it is.
   */
  private applyWindowingFilter(
    source: TimelineJSON,
    atTime?: number,
  ): {
    source: TimelineJSON;
    windowBounds: WindowBounds;
  } {
    const currentTime = clampToLastFrame(
      atTime ?? this._playbackCallbacks?.getCurrentTime() ?? 0,
      source,
    );
    // const isPlaying = this._playbackCallbacks?.getIsPlaying() ?? false;
    const isScrubbing = this._playbackCallbacks?.getIsScrubbing() ?? false;
    const { visibleElements, timeA, timeB } = collectWindowElements(
      source.elements as Record<string, any>[],
      currentTime,
      isScrubbing,
      this._windowingConfig.playback,
      this._windowingConfig.scrubbing,
    );
    logger.debug('applyWindowingFilter', { visibleElements, timeA, timeB });

    source.elements = visibleElements as any;
    return { source, windowBounds: { timeA, timeB } };
  }

  /**
   * Force refresh the video source to trigger MobX reactivity
   * This is a critical method for ensuring the UI updates when the timeline changes
   */
  refreshTimeline(atTime?: number): void {
    const reorderOpts = {
      reorderLayoutCompositionTrackToBottom: this.editor.isFlagOn(
        'reorderLayoutCompositionTrackToBottom',
      ),
    };

    const layoutOptions: LayoutOptions = {
      strategy: this._layoutStrategy,
      sceneCount: this.editor.timeline.scenes.length,
      sceneCountThreshold: this._sceneCountThreshold,
      currentTime: atTime ?? this._currentTime,
    };

    logger.debug('[refreshTimeline] toPreviewJSON', {
      strategy: layoutOptions.strategy,
      sceneCount: layoutOptions.sceneCount,
      threshold: layoutOptions.sceneCountThreshold,
      currentTime: layoutOptions.currentTime,
    });

    const previewResult = this.editor.timeline.toPreviewJSON(
      layoutOptions,
      false,
      reorderOpts,
    );

    // Cache the split-window for hybrid-adaptive playhead tracking
    this._splitWindow = previewResult.splitWindow;
    let { tempEnhancements, ...newSource } = previewResult.json;

    if (this.isWindowingActive && newSource.elements) {
      const windowingResult = this.applyWindowingFilter(newSource, atTime);
      newSource = windowingResult.source;
      this._windowBounds = windowingResult.windowBounds;
      logger.debug('[refreshTimeline] windowed source', newSource);
    }
    this.sceneLayoutIndexStore.updateFromLayoutRepresentationMap(
      tempEnhancements?.layoutRepresentationMap ?? null,
    );

    this.editor.timeline.reapplyCoupledElementBounds(
      tempEnhancements?.coupledElementBounds,
    );

    // Preserve fonts from the current source
    const currentFonts = this.videoSourceStore.source?.fonts;
    const finalSource = currentFonts
      ? { ...newSource, fonts: currentFonts }
      : newSource;

    runInAction(() => {
      this.videoSourceStore.setSource(finalSource);
    });
  }

  /**
   * Update transcript state to trigger MobX reactivity
   */
  refreshTranscript(): void {
    const editorFullTranscript = this.editor.getFullTranscript();
    this.transcriptionStore.updateFromProcessor(
      editorFullTranscript as TranscriptElementWithClipSegmentAttributes[],
    );
  }

  refreshKaraoke(): void {
    this.karaokeStore.syncFromEditor(this.editor);
  }

  refreshPunchList(): void {
    this.punchListStore?.syncFromEditor(this.editor);
  }

  setPunchListStore(store: IPunchListStore): void {
    this.punchListStore = store;
  }

  /**
   * Creates a new track
   */
  addTrack(type: 'video' | 'audio' | 'text' | 'image' = 'video'): Track {
    return this.editor.commandCreateTrack(type);
  }

  /**
   * Adds an element to a track
   */
  addElementToTrack(trackId: string, element: Element): boolean {
    return this.editor.commandAddElementToTrack(trackId, element);
  }

  /**
   * Creates a new element and adds it to a track
   */
  createAndAddElement(
    trackId: string,
    type: string,
    props: Record<string, any> = {},
  ): Element | null {
    return this.editor.commandCreateAndAddElement(trackId, type, props);
  }

  /**
   * Updates an element's property
   */
  updateElementProperty(elementId: string, key: string, value: any): boolean {
    return this.editor.commandUpdateElementProperty(elementId, key, value);
  }

  /**
   * Updates multiple element properties at once
   */
  updateElement(elementId: string, props: Partial<Element>): boolean {
    return this.editor.commandUpdateElement(elementId, props);
  }

  /**
   * Removes an element from the timeline
   * @param elementId Element ID string or Element object
   * @returns True if the element was successfully removed
   */
  removeElement(elementId: string): boolean {
    return this.editor.commandRemoveElement(elementId);
  }

  /**
   * Selects a single element (clearing previous selection)
   */
  selectElement(elementId: string): void {
    runInAction(() => {
      this.selectedElementIds = [elementId];

      // Notify observers about the selection change
      if (this.editor) {
        this.editor.notifyObservers(EditorChangeType.SELECTION_CHANGED, {
          selectedElementIds: this.selectedElementIds,
          selectedElements: this.selectedElements,
        });
      }

      // Make sure this triggers reactivity
      setTimeout(() => this.refreshTimeline(), 0);
    });
  }

  /**
   * Adds an element to the current selection
   */
  addToSelection(elementId: string): void {
    runInAction(() => {
      if (!this.selectedElementIds.includes(elementId)) {
        this.selectedElementIds.push(elementId);

        // Notify observers about the selection change
        if (this.editor) {
          this.editor.notifyObservers(EditorChangeType.SELECTION_CHANGED, {
            selectedElementIds: this.selectedElementIds,
            selectedElements: this.selectedElements,
          });
        }
      }
    });
  }

  /**
   * Alias for addToSelection for API consistency
   */
  addElementToSelection(elementId: string): void {
    this.addToSelection(elementId);
  }

  /**
   * Removes an element from the current selection
   */
  removeFromSelection(elementId: string): void {
    runInAction(() => {
      this.selectedElementIds = this.selectedElementIds.filter(
        id => id !== elementId,
      );

      // Notification happens in the deselectElement method to avoid duplicate events
    });
  }

  /**
   * Alias for removeFromSelection for API consistency
   */
  deselectElement(elementId: string): void {
    this.removeFromSelection(elementId);

    // Notify observers about the selection change
    if (this.editor) {
      this.editor.notifyObservers(EditorChangeType.SELECTION_CHANGED, {
        selectedElementIds: this.selectedElementIds,
        selectedElements: this.selectedElements,
      });
    }
  }

  /**
   * Clears the current selection
   */
  clearSelection(): void {
    runInAction(() => {
      this.selectedElementIds = [];

      // Notify observers about the selection change
      if (this.editor) {
        this.editor.notifyObservers(EditorChangeType.SELECTION_CHANGED, {
          selectedElementIds: this.selectedElementIds,
          selectedElements: this.selectedElements,
        });
      }
    });
  }

  /**
   * Gets the currently selected elements
   */
  get selectedElements(): Element[] {
    // First log how many elements should match
    const ids = this.selectedElementIds;

    // Then return elements that match the selected IDs
    const elements = this.timeline.allElements.filter(element =>
      ids.includes(element.id),
    );

    // Log how many were found
    if (ids.length > 0 && elements.length === 0) {
      logger.warn('No matching elements found for selected IDs');
    }

    return elements;
  }

  /**
   * Gets elements grouped by track number from VideoEditor.timeline.tracks.
   * This is the single source of truth for track data during editing.
   *
   * Uses timelineVersion as a reactivity trigger - when commands mutate
   * timeline.tracks, the observer increments timelineVersion, which
   * invalidates this computed, causing it to re-read the fresh data.
   *
   * @returns Map from numeric track ID to array of Elements on that track
   */
  get tracksMap(): Map<number, Element[]> {
    // Read timelineVersion to establish MobX dependency (reactivity trigger)
    void this.timelineVersion;

    const result = new Map<number, Element[]>();
    for (const track of this.editor.timeline.tracks) {
      const trackNumber = parseInt(track.id, 10);
      if (Number.isNaN(trackNumber)) continue;
      // Copy elements array to avoid exposing internal mutable state
      result.set(trackNumber, [...track.elements]);
    }
    return result;
  }

  /**
   * Returns true if karaoke elements have been generated (track 32 exists).
   * This is a reactive computed property that replaces VideoCreatorStore.isKaraokeGenerated.
   */
  get hasKaraokeTrack(): boolean {
    return this.tracksMap.has(KARAOKE_TRACK_NUMBER);
  }

  /**
   * Gets a track by ID
   */
  getTrackById(trackId: string): Track | undefined {
    return this.editor.getTrackById(trackId);
  }

  /**
   * Gets an element by ID
   */
  getElementById(elementId: string): Element | undefined {
    return this.editor.getElementById(elementId);
  }

  /**
   * Returns every timeline element as an `ElementState` snapshot, regardless
   * of whether it sits inside the renderer's current playhead window.
   * Use this in place of `videoCreator.state?.elements` whenever the read
   * needs the full timeline (max track, snap boundaries, save paths, etc.).
   */
  get allElementStates(): ElementState[] {
    return this.editor.timeline.allElements.map(el =>
      EditorStore._toElementState(el.toJSON()),
    );
  }

  /**
   * Looks up a timeline element by id and returns it as an `ElementState`,
   * including elements outside the renderer's current playhead window.
   * Use this in place of `state.elements.find(...)` whenever the target
   * element may be windowed out.
   */
  getElementStateById(elementId: string): ElementState | undefined {
    const el = this.getElementById(elementId);
    return el ? EditorStore._toElementState(el.toJSON()) : undefined;
  }

  /**
   * Returns the top-level timeline element (as `ElementState`) whose subtree
   * contains `elementId` — e.g. the composition that owns an inner/graphics
   * element. Used for sidebar tab routing. Returns undefined when the id isn't
   * a timeline element (e.g. a synthetic scene representation).
   */
  getTopLevelElementStateContaining(
    elementId: string,
  ): ElementState | undefined {
    const el = this.editor.getTopLevelElementContaining(elementId);
    return el ? EditorStore._toElementState(el.toJSON()) : undefined;
  }

  private static _toElementState(el: Record<string, any>): ElementState {
    return {
      source: { ...el, elements: undefined },
      elements: el.elements?.map(EditorStore._toElementState),
      duration: parseFloat(el.duration),
      globalTime: el.time,
      localTime: el.time,
      trimStart: parseFloat(el.trim_start || 0),
      track: el.track,
      exitDuration: 0,
    };
  }

  /**
   * Stores a timeline element snapshot in the clipboard for paste.
   */
  setElementClipboard(
    entry:
      | import('@src/services/timeline/editing/ElementClipboardTypes').ElementClipboardEntry
      | null,
  ): void {
    this.editor.setElementClipboard(entry);
  }

  /**
   * Returns the current element clipboard entry, or null if empty.
   */
  getElementClipboard():
    | import('@src/services/timeline/editing/ElementClipboardTypes').ElementClipboardEntry
    | null {
    return this.editor.getElementClipboard();
  }

  /**
   * Gets the current transcription elements
   */
  get transcriptionElements() {
    return this.transcriptionStore.transcriptionElements;
  }

  /**
   * Gets the current active transcript from the editor
   */
  get currentActiveTranscript() {
    return this.activeTranscript;
  }

  // /**
  //  * Gets the current inferred transcript from the editor
  //  * Computes it on-demand if not already cached
  //  */
  // get inferredFullTranscript() {
  //   // If we already have a cached version, return it
  //   if (
  //     this._inferredFullTranscript &&
  //     this._inferredFullTranscript.length > 0
  //   ) {
  //     return this._inferredFullTranscript;
  //   }

  //   // Otherwise, compute it directly from the editor
  //   const computed = this.editor.getInferredFullTranscript();

  //   // Cache it for future use
  //   runInAction(() => {
  //     this._inferredFullTranscript = computed;
  //   });

  //   return computed;
  // }

  // /**
  //  * Sets the inferred transcript
  //  */
  // set inferredFullTranscript(value: any[]) {
  //   this._inferredFullTranscript = value;
  // }

  /**
   * Load a project from JSON
   */
  loadProject(sources: {
    videoSource: VideoSource;
    transcript: TranscriptElementLegacy[];
    originalTranscription: TranscriptElementLegacy[];
    originalVideoTrackId: number | null;
    karaokeData: KaraokeData | null;
    scenes: IScene[] | null;
    originalVideoDuration?: string;
    extraElementData?: Video['extraElementData'];
  }): void {
    let {
      videoSource,
      transcript,
      originalTranscription,
      originalVideoTrackId,
      karaokeData,
      scenes,
      originalVideoDuration,
      extraElementData,
    } = sources;
    this.karaokeStore.reset();

    if (!karaokeData && hasKaraokeTrackInSource(videoSource)) {
      transcript = shiftLegacyTranscriptAttributesFromWhitespaces(transcript);
    }

    if (
      !karaokeData &&
      hasKaraokeTrackInSource(videoSource) &&
      this.karaokeStore.legacyKaraokeConfigLoaded
    ) {
      const migratedConfig = this.editor.createKaraokeConfig(
        this.karaokeStore.legacyKaraokeConfigLoaded,
      );
      karaokeData = parseKaraokeDataFromLegacyTranscriptElements(
        transcript,
        migratedConfig,
      );
      logger.debug('parsed karaoke data from legacy transcript');
    }

    if (!extraElementData) {
      logger.warn(
        'No extraElementData provided to loadProject - logos may not render correctly',
      );
    }
    // Always set (even if empty) to clear stale data from a prior loadProject —
    // this.editor is reused across loads. Mirrors ClipPreviewEditorStore.
    this.editor.passExtraElementData(extraElementData ?? {});

    this.editor.loadVideoSource(videoSource, originalVideoDuration);

    // NEW REMAPPING LOGIC to avoid issues with inconsistent track re-order logic between Timeline.discardLayoutTracks and remapTrackId
    const sourceVideoElement =
      Array.isArray(videoSource.elements) && originalVideoTrackId
        ? videoSource.elements.find(
            el => String(el.track) === String(originalVideoTrackId),
          )
        : null;

    const remappedVideoTrackId = sourceVideoElement
      ? this.editor.timeline.allElements.find(
          el =>
            el instanceof VideoElement &&
            String(el.id) === String(sourceVideoElement.id),
        )?.track
      : null;

    // // Remap originalVideoTrackId if tracks were renumbered during normalization
    // const remappedVideoTrackId =
    //   originalVideoTrackId !== null
    //     ? this.editor.timeline.remapTrackId(originalVideoTrackId)
    //     : null;

    // Load or auto-initialize scenes (after tracks are loaded)
    this.loadScenes(
      scenes && scenes.length > 0
        ? scenes
        : SceneInitializationService.ensureScenesInitialized(
            this.editor.timeline,
            remappedVideoTrackId ? String(remappedVideoTrackId) : undefined,
          ).map(s => s.toJSON()),
    );

    this.loadTranscript(
      transcript,
      originalTranscription,
      remappedVideoTrackId,
    );

    this.editor.getTrackManager().identifyAndMarkCoupledElements();
    this.editor.getTranscriptTrackManager().applyDefaultAudioFades();

    // should be after loading transcript as scenes bound to transcript track
    new SceneService().validateAndAdjustScenes(this.editor.timeline);

    if (karaokeData) {
      this.loadKaraokeData(karaokeData);
    } else {
      this.editor.initializeDefaultKaraokeData();
    }

    // Migration: strip auto-initialized textPosition from basic-template scenes
    // so they correctly inherit from global KaraokeConfig instead of being
    // treated as per-scene overrides.
    const defaultConfig = this.editor.getDefaultKaraokeConfig();
    if (defaultConfig) {
      migrateBasicTemplateTextPositions(
        this.editor.timeline.scenes,
        defaultConfig,
      );
    }

    runInAction(() => {
      this.isReady = true;
    });
  }

  exportProject() {
    return { sources: this.editor.exportVideoSources() };
  }

  private loadTranscript(
    transcription: TranscriptElementLegacy[] = [],
    originalTranscription: TranscriptElementLegacy[] = [],
    originalVideoTrackId: number | null = null,
  ): boolean {
    try {
      const success = this.editor.loadTranscript(
        transcription,
        originalTranscription,
        originalVideoTrackId?.toString() || undefined,
      );
      if (success) {
        this.transcriptSegmentArchitectureMigrationLog = {
          status: 'ok',
          debugInfo: {},
        };
      }
      return success;
    } catch (error) {
      logger.error('Error loading transcript:', error);
      this.transcriptSegmentArchitectureMigrationLog = {
        status: 'error',
        debugInfo: { error },
      };
      this.transcriptSegmentArchitectureNotSupported = true;
      return false;
    }
  }

  private loadKaraokeData(karaokeData: KaraokeData): boolean {
    try {
      return this.editor.loadKaraokeData(karaokeData);
    } catch (error) {
      logger.error('Error loading karaoke data:', error);
      return false;
    }
  }

  private loadScenes(scenes: IScene[]): boolean {
    try {
      return this.editor.loadScenes(scenes);
    } catch (error) {
      logger.error('Error loading layout segments:', error);
      return false;
    }
  }

  /**
   * Gets the current layout segments data for persistence
   * @returns Array of layout segment JSON objects
   */
  getScenesData(): IScene[] {
    return this.editor.timeline.scenes.map(s => s.toJSON());
  }

  /**
   * Gets the current timeline mode
   */
  get timelineMode(): TimelineMode {
    return this.editor.getTimelineMode();
  }

  /**
   * Sets the timeline mode
   */
  setTimelineMode(mode: TimelineMode): void {
    this.editor.setTimelineMode(mode);
  }

  /**
   * Undoes the last operation
   */
  undo(): boolean {
    return this.editor.undo();
  }

  /**
   * Redoes a previously undone operation
   */
  redo(): boolean {
    return this.editor.redo();
  }

  /**
   * Disconnects from the current renderer
   * Should be called when the renderer is being disposed or changed
   */
  disconnectRenderer(): void {
    // Since we don't have a direct disconnectRenderer method, we'll just
    // reset things to a clean state by refreshing the internal timeline
    this.refreshTimeline();
  }

  /**
   * Splits an element at the specified time
   * @param elementId Element ID string
   * @param splitTime Time position where to split the element (in seconds)
   * @returns An array with the two resulting elements, or null if the operation failed
   */
  splitElement(
    elementId: string,
    splitTime: number,
  ): [Element, Element] | null {
    return this.editor.commandSplitElement(elementId, splitTime);
  }

  /**
   * Shifts a track up in the layer stack
   * @param trackId The ID of the track to shift
   * @returns true if successful, false otherwise
   */
  shiftTrackLayerUp(trackId: string): boolean {
    return this.editor.commandShiftTrackLayerUp(trackId);
  }

  /**
   * Shifts a track down in the layer stack
   * @param trackId The ID of the track to shift
   * @returns true if successful, false otherwise
   */
  shiftTrackLayerDown(trackId: string): boolean {
    return this.editor.commandShiftTrackLayerDown(trackId);
  }

  resetMigrationState(): void {
    this.transcriptSegmentArchitectureNotSupported = false;
    this.transcriptSegmentArchitectureMigrationLog = null;
  }

  /**
   * Restores the transcript track by rebuilding clip segments from the
   * original transcript. Undoable via the command system.
   */
  restoreTranscriptFromOriginal(): boolean {
    return this.editor.commandRestoreTranscriptFromOriginal();
  }

  /**
   * Returns a lazily-initialized framing service for auto-framing scenes.
   * Used by commands that create scenes during restore / trim-extend; frames
   * by speaker when the speaker→face map is available.
   */
  get framingService(): ISceneFramingService {
    if (!this._framingService) {
      this._framingService = new SpeakerAwareSceneFramer(
        new SmartFramingService(
          this.editor,
          new SceneService(),
          new LayoutEngine(),
        ),
        this.getSpeakerMap,
      );
    }
    return this._framingService;
  }

  /**
   * Scene boundaries for commands that create scenes mid-edit (restore,
   * trim-extend): speaker turns + camera cuts when framing by speaker, else
   * the shot detections. See `resolveSceneDetectionsForEdit`.
   */
  sceneDetectionsForEdit(
    shots: ReadonlyArray<SceneDetection> | null | undefined,
  ): SceneDetection[] | undefined {
    return resolveSceneDetectionsForEdit({
      shots,
      transcript: this.editor.getOriginalTranscript(),
      aspectRatio: this.editor.timeline.width / this.editor.timeline.height,
      speakerMap: this.getSpeakerMap(),
    });
  }
}
